/**
 * 共用プルダウン要素
 * @param {String} name react-hook-form管理用フォーム特定文字列
 * @param {String} placeholder プレースホルダー表示用文字列
 * @param {Object[]} options プルダウン表示用オブジェクト配列
 * @param {Object} rules バリデーション設定オブジェクト
 * @param {Object} wrapperCss ラッパーのスタイル設定用オブジェクト
 */

/** @jsxImportSource @emotion/react */
import { memo, useCallback } from "react";
import { Controller, useFormContext } from "react-hook-form";
import Select, { components } from "react-select";

import ValidationErrorText from "components/ui/validation/ValidationErrorText";

// 入力フォーム用のcss読み込み
import { selectIconActive } from "assets/css/stylesCss";
import { selectIconDisable } from "assets/css/stylesCss";

// 共用プルダウン要素
const CommonPulldown = memo(
  ({ name, placeholder, options, rules, wrapperCss, isDisabled }) => {
    const {
      formState: { errors },
      control,
      getValues,
      setValue,
    } = useFormContext();

    const getSelectedValue = useCallback(
      (value) => {
        const selectedValue = options.find((x) => x.value === value);
        return selectedValue ? selectedValue : null;
      },
      [options]
    );

    return (
      <div css={wrapperCss}>
        {/* プルダウンリスト */}
        <Controller
          name={name}
          rules={rules}
          control={control}
          render={({ field }) => (
            <Select
              isDisabled={isDisabled}
              components={{ DropdownIndicator }}
              value={
                isDisabled ? null : getSelectedValue(field.value)
              }
              options={options}
              isSearchable={false}
              placeholder={placeholder}
              onChange={(newValue) => {
                field.onChange(newValue?.value);
                setValue(name, getValues(name), {
                  shouldValidate: true,
                });
                setValue(name + "Label", newValue?.label);
              }}
              onMenuClose={() => {
                setValue(name, getValues(name), {
                  shouldValidate: true,
                });
              }}
              // スタイル指定、エラー時のみ赤枠表示
              styles={{
                ...customStyles,
                control: isDisabled
                  ? (provided, state) => ({
                    ...customStyles.control(provided, state),
                    backgroundColor: "var(--mtg-grey-3)",
                    cursor: "default",
                    "&:hover": {
                      backgroundColor: "var(--mtg-grey-3)",
                    },
                  })
                  : !errors[name]
                    ? customStyles.control
                    : (provided, state) => ({
                      ...customStyles.control(provided, state),
                      borderColor: "var(--mtg-live-red)",
                      borderWidth: state.isFocused ? "2px" : "1px",
                      "&:hover": {
                        borderColor: "var(--mtg-live-red)",
                        borderWidth: "2px",
                      },
                    }),
              }}
            />
          )}
        />
        {/* エラー表示 */}
        {errors[name] && (
          <ValidationErrorText>{errors[name].message}</ValidationErrorText>
        )}
      </div>
    );
  }
);

// ドロップダウンリスト右端のV字アイコン表示設定
const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      {props.isDisabled ? <div css={selectIconDisable}></div> : <div css={selectIconActive}></div>}
    </components.DropdownIndicator>
  );
};

// react-selectプルダウン要素のスタイリング指定
// ライブラリの都合上cssライクな記述ができず他のスタイル指定と異なる形式のため共用cssファイルに切り出さずこのファイル内で管理
const customStyles = {
  control: (provided, state) => ({
    ...provided,
    borderColor: state.isFocused ? "var(--mtg-urbanblue)" : "var(--mtg-grey-3)",
    "&:hover": {
      borderColor: "var(--mtg-urbanblue)",
    },
    boxShadow: "none",
    display: "flex",
    borderRadius: "var(--mtg-border-radius)",
    height: "56px",
    cursor: "pointer",
  }),
  menu: () => ({
    border: "1px solid var(--mtg-urbanblue)",
    borderRadius: "var(--mtg-border-radius)",
    zIndex: 5,
    overflow: "hidden",
    position: "absolute",
    width: "100%",
    filter: "drop-shadow(0px 8px 16px rgba(24, 26, 36, 0.25))",
  }),
  menuList: (provided) => ({
    ...provided,
    padding: "0",
    border: "none",
    borderRadius: "var(--mtg-border-radius)",
    backgroundColor: "var(--mtg-white)",
  }),
  option: (provided, state) => ({
    ...provided,
    position: "relative",
    color: "var(--mtg-midnight)",
    backgroundColor: state.isSelected
      ? "var(--mtg-blue-3)"
      : state.isFocused
        ? "var(--mtg-blue-4)"
        : "var(--mtg-white)",
    padding: "16px",
    height: "56px",
    "&::before": {
      position: "absolute",
      content: '""',
      top: "-1px",
      left: "16px",
      width: "calc(100% - 32px)",
      display: "inline-block",
      borderTop: "1px solid var(--mtg-grey-3)",
    },
    marginTop: "1px",
    "&:first-of-type::before": {
      borderTop: "0",
    },
    transition: "background-color 0.25s",
    cursor: "pointer",
  }),
  singleValue: (provided) => ({ ...provided, marginLeft: "5px" }),
  placeholder: (provided) => ({ ...provided, color: "var(--mtg-grey-2)" }),
  indicatorSeparator: () => ({ display: "none" }),
};

export default CommonPulldown;
